/**
 * Dynamic Product Status System
 * Handles real-time status updates based on inventory levels and quantity changes
 */

// Lead time copy - dynamic-product-status.liquid publishes window.ZH_LEAD_TIMES
// from the "Lead times" theme settings; fall back to the standard windows.
function zhLeadTimes() {
  const overrides = window.ZH_LEAD_TIMES || {};
  return {
    madeToOrder: overrides.madeToOrder || 'Delivery in 8-10 weeks',
    inTransit: overrides.inTransit || 'Delivery in 8–10 weeks'
  };
}

class DynamicProductStatus {
  constructor() {
    this.statusElements = new Map();
    this.init();
  }

  init() {
    this.findStatusElements();
    this.setupQuantityListeners();
    this.setupVariantListeners();
    this.updateAllStatuses();
  }

  findStatusElements() {
    const elements = document.querySelectorAll('.dynamic-status');
    elements.forEach(element => {
      const variantId = element.dataset.variantId;
      if (variantId) {
        this.statusElements.set(variantId, element);
      }
    });
  }

  setupQuantityListeners() {
    document.addEventListener('change', (event) => {
      if (event.target.matches('input[name="quantity"]') || event.target.matches('input[name="updates[]"]')) {
        this.handleQuantityChange(event.target);
      }
    });

    document.addEventListener('click', (event) => {
      if (event.target.matches('.quantity__button')) {
        setTimeout(() => {
          const quantityContainer = event.target.closest('.quantity');
          if (quantityContainer) {
            const quantityInput = quantityContainer.querySelector('input[name="quantity"]') ||
              quantityContainer.querySelector('input[name="updates[]"]');
            if (quantityInput) {
              this.handleQuantityChange(quantityInput);
            }
          }
        }, 100);
      }
    });
  }

  setupVariantListeners() {
    document.addEventListener('change', (event) => {
      if (event.target.matches('input[name="id"]')) {
        this.handleVariantChange(event.target.value);
      }
    });
  }

  handleQuantityChange(quantityInput) {
    const quantity = parseInt(quantityInput.value) || 1;
    const form = quantityInput.closest('form');
    const variantId = form?.querySelector('input[name="id"]')?.value;

    if (!variantId) {
      const cartItem = quantityInput.closest('.cart-item');
      if (cartItem) {
        const statusBadge = cartItem.querySelector('.dynamic-status');
        if (statusBadge && statusBadge.dataset.variantId) {
          const cartVariantId = statusBadge.dataset.variantId;
          this.updateStatusForVariant(cartVariantId, quantity);
          return;
        }
      }
    }

    if (variantId) {
      this.updateStatusForVariant(variantId, quantity);
    }
  }

  handleVariantChange(variantId) {
    const quantityInput = document.querySelector('input[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
    this.updateStatusForVariant(variantId, quantity);
  }

  updateStatusForVariant(variantId, quantity) {
    const statusElement = this.statusElements.get(variantId);
    if (!statusElement) return;

    const currentInventory = parseInt(statusElement.dataset.inventory) || 0;
    const baseStatus = statusElement.dataset.baseStatus || '';
    const checkInventory = statusElement.dataset.checkInventory === 'true';

    statusElement.classList.add('updating');

    const newStatus = this.calculateStatus(baseStatus, currentInventory, quantity, checkInventory);
    this.updateStatusElement(statusElement, newStatus, quantity);

    setTimeout(() => {
      statusElement.classList.remove('updating');
    }, 300);
  }

  calculateStatus(baseStatus, inventory, quantity, checkInventory) {
    const baseStatusLower = (baseStatus || '').toLowerCase().trim();
    const isMadeToOrder = baseStatusLower.includes('made to order') || baseStatusLower.includes('made-to-order');

    // 1. Made to Order is intrinsic — inventory is irrelevant.
    if (isMadeToOrder) {
      return {
        status: 'Made to Order',
        class: 'product-status-icon--made-to-order',
        message: zhLeadTimes().madeToOrder,
        isMixed: false
      };
    }

    // 2. Inventory-tracked → derive status LIVE from inventory (mirrors dynamic-product-status.liquid).
    if (checkInventory) {
      if (inventory > 0 && quantity <= inventory) {
        return {
          status: 'In Stock',
          class: 'product-status-icon--in-stock',
          message: 'Ships within 5-7 business days',
          isMixed: false
        };
      } else if (inventory > 0) {
        return {
          status: 'Mixed Stock',
          class: 'product-status-icon--mixed-stock',
          message: `partial stock | ${inventory} boxes in stock, ${quantity - inventory} boxes in transit`,
          isMixed: true
        };
      }
      return {
        status: 'In Transit',
        class: 'product-status-icon--in-transit',
        message: zhLeadTimes().inTransit,
        isMixed: false
      };
    }

    // 3. Inventory not tracked → fall back to the metafield text.
    if (baseStatusLower === 'in stock') {
      return {
        status: 'In Stock',
        class: 'product-status-icon--in-stock',
        message: 'Ships within 5-7 business days',
        isMixed: false
      };
    }
    return {
      status: 'In Transit',
      class: 'product-status-icon--in-transit',
      message: zhLeadTimes().inTransit,
      isMixed: false
    };
  }

  updateStatusElement(element, newStatus, quantity) {
    const icon = element.querySelector('.product-status-icon');
    const text = element.querySelector('.product-status-text');

    if (icon && text) {
      icon.className = `product-status-icon ${newStatus.class}`;

      const svg = icon.querySelector('svg');
      if (svg) {
        if (newStatus.isMixed) {
          svg.innerHTML = `
            <rect x="2" y="20" width="40" height="24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
            <rect x="42" y="28" width="18" height="16" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="18" cy="48" r="5" fill="none" stroke="currentColor" stroke-width="3"/>
            <circle cx="46" cy="48" r="5" fill="none" stroke="currentColor" stroke-width="3"/>
            <rect x="8" y="8" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          `;
        } else {
          svg.innerHTML = `
            <rect x="2" y="20" width="40" height="24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
            <rect x="42" y="28" width="18" height="16" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="18" cy="48" r="5" fill="none" stroke="currentColor" stroke-width="3"/>
            <circle cx="46" cy="48" r="5" fill="none" stroke="currentColor" stroke-width="3"/>
          `;
        }
      }

      text.innerHTML = `<strong>${newStatus.status}</strong> | ${newStatus.message}`;
    }

    element.dataset.quantity = quantity;
  }

  updateAllStatuses() {
    this.statusElements.forEach((element, variantId) => {
      const quantity = parseInt(element.dataset.quantity) || 1;
      this.updateStatusForVariant(variantId, quantity);
    });
  }

  refreshVariantStatus(variantId, inventory, baseStatus) {
    const element = this.statusElements.get(variantId);
    if (element) {
      element.dataset.inventory = inventory;
      element.dataset.baseStatus = baseStatus;
      const quantity = parseInt(element.dataset.quantity) || 1;
      this.updateStatusForVariant(variantId, quantity);
    }
  }
}

let dynamicProductStatusInstance = null;

function initDynamicProductStatus() {
  if (dynamicProductStatusInstance) {
    dynamicProductStatusInstance.findStatusElements();
    dynamicProductStatusInstance.updateAllStatuses();
  } else {
    dynamicProductStatusInstance = new DynamicProductStatus();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initDynamicProductStatus();

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          const hasNewStatusBadges = Array.from(mutation.addedNodes).some(node => {
            if (node.nodeType === 1) {
              return node.querySelector?.('.dynamic-status') || node.classList?.contains('dynamic-status');
            }
            return false;
          });

          if (hasNewStatusBadges && dynamicProductStatusInstance) {
            setTimeout(() => {
              dynamicProductStatusInstance.findStatusElements();
              dynamicProductStatusInstance.updateAllStatuses();
            }, 100);
          }
        }
      });
    });

    const cartDrawerBody = cartDrawer.querySelector('#CartDrawer-Body');
    if (cartDrawerBody) {
      observer.observe(cartDrawerBody, {
        childList: true,
        subtree: true
      });
    }
  }
});

window.DynamicProductStatus = DynamicProductStatus;
window.initDynamicProductStatus = initDynamicProductStatus;
